import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Directive that displays a dollar sign in the input when the user types numbers,
 * removes the dollar sign when the value is empty, and ensures only the numeric
 * value (no $) is stored in the form control. Enable via [appCurrencyInput]="true" from the parent.
 */
@Directive({
    selector: '[appCurrencyInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencyInputDirective),
            multi: true,
        },
    ],
    standalone: false
})
export class CurrencyInputDirective implements ControlValueAccessor, OnInit {
  @Input() appCurrencyInput = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private lastDisplayValue = '';

  constructor(private el: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {
    if (this.appCurrencyInput) {
      this.lastDisplayValue = this.getDisplayValue(this.el.nativeElement.value);
    }
  }

  private getDigitsOnly(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '';
    }
    const str = this.appCurrencyInput
      ? String(value).replace(/[^0-9.]/g, '')
      : String(value).replace(/[^0-9.\-]/g, '');
    // Allow at most one decimal point
    const parts = str.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return str;
  }

  private getDisplayValue(digits: string): string {
    if (!digits || digits === '') {
      return '';
    }
    return this.appCurrencyInput ? '$ ' + digits : digits;
  }

  writeValue(value: string | number | null | undefined): void {
    const digits = this.getDigitsOnly(value);
    const display = this.getDisplayValue(digits);
    this.lastDisplayValue = display;
    this.el.nativeElement.value = display;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input')
  onInput(): void {
    const input = this.el.nativeElement;
    const raw = input.value || '';

    if (this.appCurrencyInput) {
      const digits = this.getDigitsOnly(raw);
      const display = this.getDisplayValue(digits);
      if (input.value !== display) {
        input.value = display;
        this.lastDisplayValue = display;
      } else {
        this.lastDisplayValue = display;
      }
      this.onChange(digits === '' ? null : digits);
    } else {
      this.onChange(raw === '' ? null : raw);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }
}
